import React from 'react';
import { EditorContent } from '@tiptap/react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code,
  Undo,
  Redo,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
} from '@mui/icons-material';

const TiptapEditor = ({ editor, height = '300px' }) => {
  if (!editor) {
    return null;
  }

  // Check if extension is available
  const hasExtension = (name) => {
    return editor.extensionManager.extensions.some(ext => ext.name === name);
  };

  const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            borderRadius: '6px',
            color: active ? '#4f46e5' : '#64748b',
            backgroundColor: active ? '#eef2ff' : 'transparent',
            '&:hover': {
              backgroundColor: active ? '#e0e7ff' : '#f1f5f9',
            },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );

  // Safe color getter
  const getTextColor = () => {
    try {
      return editor.getAttributes('textStyle')?.color || '#000000';
    } catch {
      return '#000000';
    }
  };

  // Safe highlight color getter
  const getHighlightColor = () => {
    try {
      return editor.getAttributes('highlight')?.color || '#ffff00';
    } catch {
      return '#ffff00';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          p: 1,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}
      >
        {/* Text Formatting */}
        <ToolbarButton
          title="Bold (Ctrl+B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <FormatBold sx={{ fontSize: 18 }} />
        </ToolbarButton>

        <ToolbarButton
          title="Italic (Ctrl+I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <FormatItalic sx={{ fontSize: 18 }} />
        </ToolbarButton>

        {hasExtension('underline') && (
          <ToolbarButton
            title="Underline (Ctrl+U)"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
          >
            <FormatUnderlined sx={{ fontSize: 18 }} />
          </ToolbarButton>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Headings */}
        <ToolbarButton
          title="Heading 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
        >
          <Box sx={{ fontSize: 14, fontWeight: 700 }}>H1</Box>
        </ToolbarButton>

        <ToolbarButton
          title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
        >
          <Box sx={{ fontSize: 13, fontWeight: 600 }}>H2</Box>
        </ToolbarButton>

        <ToolbarButton
          title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
        >
          <Box sx={{ fontSize: 12, fontWeight: 600 }}>H3</Box>
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Lists */}
        <ToolbarButton
          title="Bullet List"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          <FormatListBulleted sx={{ fontSize: 18 }} />
        </ToolbarButton>

        <ToolbarButton
          title="Numbered List"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          <FormatListNumbered sx={{ fontSize: 18 }} />
        </ToolbarButton>

        <ToolbarButton
          title="Quote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
        >
          <FormatQuote sx={{ fontSize: 18 }} />
        </ToolbarButton>

        <ToolbarButton
          title="Code Block"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
        >
          <Code sx={{ fontSize: 18 }} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Text Color - Only show if Color extension is available */}
        {hasExtension('color') && (
          <Tooltip title="Text Color">
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="color"
                onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                value={getTextColor()}
                style={{
                  width: 28,
                  height: 28,
                  padding: 0,
                  border: '1px solid #e2e8f0',
                  borderRadius: 4,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                }}
              />
            </Box>
          </Tooltip>
        )}

        {/* Highlight Color - Only show if Highlight extension is available */}
        {hasExtension('highlight') && (
          <Tooltip title="Highlight Color">
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="color"
                onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
                value={getHighlightColor()}
                style={{
                  width: 28,
                  height: 28,
                  padding: 0,
                  border: '1px solid #e2e8f0',
                  borderRadius: 4,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                }}
              />
            </Box>
          </Tooltip>
        )}

        {(hasExtension('color') || hasExtension('highlight')) && (
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        )}

        {/* Text Alignment - Only show if TextAlign extension is available */}
        {hasExtension('textAlign') && (
          <>
            <ToolbarButton
              title="Align Left"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              active={editor.isActive({ textAlign: 'left' })}
            >
              <FormatAlignLeft sx={{ fontSize: 18 }} />
            </ToolbarButton>

            <ToolbarButton
              title="Align Center"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              active={editor.isActive({ textAlign: 'center' })}
            >
              <FormatAlignCenter sx={{ fontSize: 18 }} />
            </ToolbarButton>

            <ToolbarButton
              title="Align Right"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              active={editor.isActive({ textAlign: 'right' })}
            >
              <FormatAlignRight sx={{ fontSize: 18 }} />
            </ToolbarButton>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}

        {/* Undo/Redo */}
        <ToolbarButton
          title="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo sx={{ fontSize: 18 }} />
        </ToolbarButton>

        <ToolbarButton
          title="Redo (Ctrl+Y)"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo sx={{ fontSize: 18 }} />
        </ToolbarButton>
      </Box>

      {/* Editor Content */}
      <Box
        sx={{
          '& .ProseMirror': {
            minHeight: height,
            maxHeight: '500px',
            overflowY: 'auto',
            padding: '16px',
            outline: 'none',
            fontSize: '14px',
            lineHeight: 1.7,
            color: '#1e293b',
            '& p': { margin: '0 0 12px 0' },
            '& h1': { fontSize: '24px', fontWeight: 700, margin: '16px 0 12px 0' },
            '& h2': { fontSize: '20px', fontWeight: 600, margin: '14px 0 10px 0' },
            '& h3': { fontSize: '16px', fontWeight: 600, margin: '12px 0 8px 0' },
            '& ul, & ol': { paddingLeft: '24px', margin: '8px 0' },
            '& li': { margin: '4px 0' },
            '& blockquote': {
              borderLeft: '3px solid #a5b4fc',
              paddingLeft: '16px',
              margin: '12px 0',
              color: '#64748b',
              fontStyle: 'italic',
              backgroundColor: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '0 8px 8px 0',
            },
            '& code': {
              backgroundColor: '#f1f5f9',
              padding: '2px 6px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '13px',
            },
            '& pre': {
              backgroundColor: '#1e293b',
              color: '#e2e8f0',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
            },
            '& a': { color: '#4f46e5', textDecoration: 'underline' },
            // Support for colored text and highlights
            '& span[style*="color"]': { /* allows inline color styles */ },
            '& mark': { borderRadius: '2px', padding: '0 2px' },
            // Text alignment styles
            '& [style*="text-align: center"], & .text-center': { textAlign: 'center' },
            '& [style*="text-align: right"], & .text-right': { textAlign: 'right' },
            '& [style*="text-align: left"], & .text-left': { textAlign: 'left' },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default TiptapEditor;